import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Link, Navigate } from 'react-router-dom';

import { MachineActions } from 'redux/actions/settings/MachineActions';
import { MachineMaintenanceActions } from 'redux/actions/settings/MachineMaintenanceActions';
import { MachineHistoryActions } from 'redux/actions/machines/MachineHistoryActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition, faCar, faCheck, faTools, faClipboard } from '@fortawesome/free-solid-svg-icons'

import { StoreModel } from 'models/StoreModel';
import { LoginUser } from 'models/LoginUser';
import { Machine } from 'models/Machine';
import { MachineMaintenance } from 'models/MachineMaintenance';
import { MachineHistory } from 'models/MachineHistory';
import Translator from 'app/components/common/Translator';
import { PageContentHeader } from 'app/components/common/PageContentHeader';
import { MachineTypeActions } from 'redux/actions/settings/MachineTypeActions';
import { MaintenanceActions } from 'redux/actions/settings/MaintenanceActions';
import { ManufacturerActions } from 'redux/actions/settings/ManufacturerActions';
import StartCard from 'app/pages/Dashboard/StartCard';
import { DailyReportCard, dueItems, newestFirst } from 'app/pages/Dashboard/DailyReportCard';
import { Translations } from 'models/Translations';
import { FuelType } from 'models/FuelType';
import { FuelTypeActions } from 'redux/actions/settings/FuelTypeActions';
import { UserRoleTypes } from 'models/UserRole';

interface Props {
    fetchMachineMaintenances: typeof MachineMaintenanceActions.fetchMachineMaintenances;
    fetchMachines: typeof MachineActions.fetchMachines;
    fetchMachineHistories: typeof MachineHistoryActions.fetchMachineHistories;
    fetchMachineTypes: typeof MachineTypeActions.fetchMachineTypes;
    fetchMaintenances: typeof MaintenanceActions.fetchMaintenances;
    fetchManufacturers: typeof ManufacturerActions.fetchManufacturers;
    loginUser: LoginUser;
    machineMaintenances: MachineMaintenance[];
    machines: Machine[];
    machineHistories: MachineHistory[];
    fuelTypes: FuelType[];
    fetchFuelTypes: typeof FuelTypeActions.fetchFuelTypes;
    translations: Translations;
}

class Dashboard extends React.Component<Props> {
    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.machineMaintenances) {
                this.props.fetchMachineMaintenances(this.props.loginUser.token);
            }
            if (!this.props.machines) {
                this.props.fetchMachines(this.props.loginUser.token);
            }
            if (!this.props.machineHistories) {
                this.props.fetchMachineHistories(this.props.loginUser.token);
            }
            if (!this.props.fuelTypes) {
                this.props.fetchFuelTypes(this.props.loginUser.token);
            }

        }
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.machineMaintenances && this.props.machines && this.props.machineHistories
    }

    calculateOperativeMachinery() {
        let defects = 0;
        this.props.machines.forEach(ma => {
            let isDefect = false;
            const mHsWithdefects = this.props.machineHistories.filter(mh => mh.machine.id == ma.id && mh.machineDefects && mh.machineDefects.length > 0);
            if (mHsWithdefects && mHsWithdefects.length > 0) {
                mHsWithdefects.forEach(mh => {
                    // only open defects that take the machine out of order count
                    mh.machineDefects?.filter(md => md.outOfOrderFlag && !md.archivedDate).forEach(md => {
                        if (!this.props.machineHistories.find(mh => mh.fixedMachineDefects?.find(fd => fd.id === md.id))) {
                            isDefect = true;
                        }
                    })
                })
            }
            if (isDefect) {
                defects++
            }
        });
        return this.props.machines.length - defects;
    }

    calculateMaintenanceDue() {
        return this.props.machines.filter(ma => !ma.archivedDate).reduce((due, ma) => {
            const histories = this.props.machineHistories.filter(mh => mh.machine.id === ma.id && !mh.archivedDate).sort(newestFirst);
            return due + dueItems(ma, histories, this.props.machineMaintenances, "").filter(i => i.warning).length;
        }, 0);
    }

    calculateFixesLast7days() {
        const relevantMhs = this.props.machineHistories.filter(mh => (mh.fixedMachineDefects && mh.fixedMachineDefects.length > 0)
            || (mh.machineMaintenances && mh.machineMaintenances.length > 0));
        if (relevantMhs && relevantMhs.length > 0) {
            const oneDay = 24 * 60 * 60 * 1000;
            const last7days = relevantMhs.filter(mh => Math.abs((new Date().getTime() - new Date(mh.date).getTime()) / oneDay) <= 7);
            return last7days ? last7days.length : 0;
        }
        return 0;
    }

    isPoolManager() {
        return !!this.props.loginUser.userData?.roles.find(r => r.name === UserRoleTypes.poolManager);
    }

    // a tile without a target (no permission for the page) is rendered as plain box
    tile(to: string | undefined, modifier: string, value: React.ReactNode, label: string, icon: IconDefinition) {
        const content = (
            <>
                <div className="c-dashboard__card--content">
                    <div className="c-dashboard__card--value">{value}</div>
                    <div className="c-dashboard__card--label"><Translator text={label} /></div>
                </div>
                <div className="c-dashboard__card--icon">
                    <FontAwesomeIcon icon={icon}></FontAwesomeIcon>
                </div>
            </>
        );
        const className = "c-dashboard__card c-dashboard__card--" + modifier;
        return to ? <Link to={to} className={className}>{content}</Link> : <div className={className}>{content}</div>;
    }

    // pool managers page through the fleet, everyone else sees the machine assigned to them
    reportMachines(): Machine[] {
        if (this.isPoolManager()) {
            return this.props.machines.filter(m => !m.archivedDate);
        }
        const assigned = Machine.assignedTo(this.props.machines, this.props.loginUser.userData?.id);
        return assigned ? [assigned] : [];
    }

    isNewUser() {
        if (this.props.machineHistories.length === 0 && this.props.loginUser.userData && this.props.loginUser.userData.roles.find(r => r.name === UserRoleTypes.poolManager)) {
            return true;
        }
        return false;
    }

    render() {
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text="MOPS Dashboard" />
                    <Translator text="Loading" />
                </div>
            )
        }
        const operativeMachinery = this.calculateOperativeMachinery();
        const totalMachinery = this.props.machines.length
        const maintenanceDue = this.calculateMaintenanceDue();
        const fixes7Days = this.calculateFixesLast7days();
        const openTasks = 0;
        return (
            !this.loggedIn() ? <Navigate replace to='/account/login' /> :
                <div className="c-page__content">
                    <PageContentHeader text="MOPS Dashboard" />
                    {this.ready() &&
                        <div className="c-dashboard">
                            <div className="c-card">
                                <div className="c-dashboard__cards">
                                    {this.tile(this.isPoolManager() ? "/Machines" : undefined, "operative", operativeMachinery + " / " + totalMachinery, "Operative Machinery", faCar)}
                                    {this.tile("/Machines/UpcomingMaintenance", "maintenance-due", maintenanceDue, "Maintenance Due", faTools)}
                                    {this.tile("/Machines/histories", "fixes", fixes7Days, "Fixed/Maintained last 7 Days", faCheck)}
                                    {this.tile("/Machines/workshop", "open-tasks", openTasks, "Open Tasks", faClipboard)}
                                </div>
                            </div>
                            <DailyReportCard machines={this.reportMachines()} machineHistories={this.props.machineHistories} machineMaintenances={this.props.machineMaintenances} fuelTypes={this.props.fuelTypes} translations={this.props.translations} />
                            {this.isNewUser() &&
                                <StartCard />
                            }
                        </div>
                    }
                </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        machines: state.machines,
        machineMaintenances: state.machineMaintenances,
        machineHistories: state.machineHistories,
        translations: state.translations,
        fuelTypes: state.fuelTypes,
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchMachines: bindActionCreators(MachineActions.fetchMachines, dispatch),
        fetchMachineMaintenances: bindActionCreators(MachineMaintenanceActions.fetchMachineMaintenances, dispatch),
        fetchMachineHistories: bindActionCreators(MachineHistoryActions.fetchMachineHistories, dispatch),
        fetchFuelTypes: bindActionCreators(FuelTypeActions.fetchFuelTypes, dispatch),
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Dashboard);